import React, { useState, useEffect, useRef } from 'react';
import { Button, Collapse, Form, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './filter.scss';

/**
 * Dynamic Filter component.
 * Only supports: 'checkbox' and 'date' types (YYYY-MM-DD format).
 */
const Filter = ({ items, filter, onFilterChange, fields }) => {
  // Initialize filter values
  const initValues = () => fields.reduce((acc, f) => {
    acc[f.key] = f.type === 'checkbox'
      ? (filter[f.key] || [])
      : (filter[f.key] || '');
    return acc;
  }, {});

  // Initialize collapse states
  const initOpen = () => fields.reduce((acc, f) => {
    acc[f.key] = false;
    return acc;
  }, {});

  const [values, setValues] = useState(initValues);
  const [openMap, setOpenMap] = useState(initOpen);

  // Sync values when external filter prop changes
  useEffect(() => {
    setValues(initValues());
  }, [filter]);

  // Reset collapse when fields list changes
  useEffect(() => {
    setOpenMap(initOpen());
  }, [fields]);

  // Debounce filter change callback
  const debounceRef = useRef();
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onFilterChange(values), 300);
    return () => clearTimeout(debounceRef.current);
  }, [values, onFilterChange]);

  // Get unique options for checkboxes
  const uniqueOptions = key => Array.from(
    new Set(items.map(i => i[key]).filter(Boolean))
  );

  const toggle = key => () => {
    setOpenMap(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCheckbox = key => e => {
    e.stopPropagation();
    const { value, checked } = e.target;
    setValues(prev => ({
      ...prev,
      [key]: checked
        ? [...prev[key], value]
        : prev[key].filter(v => v !== value)
    }));
  };

  const handleDate = key => e => {
    const raw = e.target.value;
    setValues(prev => ({ ...prev, [key]: raw }));
  };

  const clearAll = () => {
    const reset = fields.reduce((acc, f) => {
      acc[f.key] = f.type === 'checkbox' ? [] : '';
      return acc;
    }, {});
    setValues(reset);
    onFilterChange(reset);
  };

  return (
    <Container className="filter-container">
      <Button onClick={clearAll} variant="outline-primary" className="mb-3">
        Clear All Filters
      </Button>
      <Form>
        {fields.map(({ key, label, type, collapse = true }) => {
          const opts = type === 'checkbox' ? uniqueOptions(key) : [];
          const isOpen = collapse ? openMap[key] : true;
          return (
            <Form.Group key={key} controlId={`${key}Filter`} className="bordered-group">
              <Button
                variant="outline-secondary"
                className="w-100 text-left no-border statusFilterButton"
                onClick={collapse ? toggle(key) : undefined}
                aria-controls={`${key}-collapse`}
                aria-expanded={isOpen}
              >
                {label} <span className={`arrow ${isOpen ? 'up' : 'down'}`} />
              </Button>
              <Collapse in={isOpen}>
                <div id={`${key}-collapse`} className="collapseContainer" onClick={e => e.stopPropagation()}>
                  {type === 'checkbox' && opts.map((opt, idx) => (
                    <Form.Check
                      key={idx}
                      id={`${key}-${idx}`}
                      type="checkbox"
                      label={opt}
                      value={opt}
                      checked={Array.isArray(values[key]) && values[key].includes(opt)}
                      onChange={handleCheckbox(key)}
                    />
                  ))}
                  {type === 'date' && (
                    <Form.Control
                      type="text"
                      placeholder="YYYY-MM-DD"
                      pattern="\\d{4}-\\d{2}-\\d{2}"
                      value={values[key]}
                      onChange={handleDate(key)}
                    />
                  )}
                </div>
              </Collapse>
            </Form.Group>
          );
        })}
      </Form>
    </Container>
  );
};

Filter.propTypes = {
  items: PropTypes.array.isRequired,
  filter: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['checkbox', 'date']).isRequired,
      collapse: PropTypes.bool
    })
  ).isRequired
};

export default Filter;